import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVITY_LOG } from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

/**
 * Account → Activity log (Prototype v13): one plain sentence per event — what
 * it was about by name, and an approval's version and override — then who, and
 * an area chip. The server writes the sentences (platform/services/
 * activityLog.js): only it can look up the article, post or campaign by id.
 *
 * Content is a fourth area beside the design's Approval / Agent / Settings: a
 * person writing an article or a campaign is neither a setting nor an approval.
 */

type Area = 'approval' | 'agent' | 'content' | 'settings';
type Filter = 'all' | Area;

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'approval', label: 'Approvals' },
  { id: 'agent', label: 'Agent actions' },
  { id: 'content', label: 'Content changes' },
  { id: 'settings', label: 'Settings changes' },
];

const AREA: Record<Area, string> = {
  approval: 'Approval',
  agent: 'Agent',
  content: 'Content',
  settings: 'Settings',
};

interface ActivityEntry {
  id: string;
  at: string;
  what: string;
  who: string;
  area: Area;
}

function whenLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === now.toDateString()) return `Today ${time}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return `Yesterday ${time}`;
  const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return date.getFullYear() === now.getFullYear() ? day : `${day}, ${date.getFullYear()}`;
}

/** The server marks a time inside a sentence as {{t:<ISO>}}; show it in this reader's zone. */
export function localTimes(text: string): string {
  return text.replace(/\{\{t:([^}]+)\}\}/g, (_, iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? 'a later time'
      : d.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
  });
}

export function EventAuditSettings() {
  const [filter, setFilter] = useState<Filter>('all');
  const { accountId, isLoading: accountsLoading } = useAccount();
  const { data, loading, error } = useQuery<{ activityLog: ActivityEntry[] }>(GET_ACTIVITY_LOG, {
    variables: { accountId, limit: 200 },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const rows = useMemo(
    () =>
      (data?.activityLog || []).map((e) => ({
        ...e,
        what: localTimes(e.what),
        when: whenLabel(e.at),
        label: AREA[e.area] || AREA.content,
      })),
    [data?.activityLog]
  );

  const shown = filter === 'all' ? rows : rows.filter((row) => row.area === filter);

  if (accountsLoading || (loading && !data)) return <Loader label="Loading" className="ink-lede" />;

  return (
    <div>
      <h1 className="ink-h1">Activity log</h1>
      <p className="ink-lede">
        Every change to this account, by people and agents. Approvals record the exact version and
        any override.
      </p>
      {error ? <p className="ink-lede">The activity log didn’t load. Try again.</p> : null}
      <div className="ink-act-filters">
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            className="ink-chip"
            data-active={filter === item.id ? 'true' : 'false'}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="ink-act">
        {shown.length === 0 ? (
          <p className="ink-lede" style={{ marginTop: 28 }}>
            {rows.length === 0 ? 'No activity yet.' : 'Nothing in this view.'}
          </p>
        ) : (
          shown.map((row) => (
            <div className="ink-act-row" key={row.id}>
              <span className="ink-act-when">{row.when}</span>
              <div>
                <span className="ink-act-what">{row.what}</span>
                <span className="ink-act-who"> · {row.who}</span>
              </div>
              <span className="ink-act-area">{row.label}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
